<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import InteractiveMessageBuilderModal from 'dashboard/components/widgets/conversation/InteractiveMessageBuilder/Modal.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    AttachmentPreview,
    EmojiInput,
    InteractiveMessageBuilderModal,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      messageType: 'text', // 'text', 'interactive', or 'multimedia'
      interactiveChannelType: 'web', // 'web', 'whatsapp', 'telegram', or 'facebook' - which channels can use this
      selectedInboxId: null, // Selected inbox for fetching catalogs/flows
      showInteractiveBuilder: false, // Toggle for interactive builder modal
      interactivePayload: null, // Stores the built interactive message payload
      attachedFiles: [], // For multimedia messages
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
      showEmojiPicker: false,
      showRichTextToolbar: false,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      inboxesList: 'inboxes/getInboxes',
    }),
    isFormValid() {
      if (this.messageType === 'interactive') {
        return !this.v$.shortCode.$invalid && this.interactivePayload !== null;
      }
      if (this.messageType === 'multimedia') {
        return (
          !this.v$.shortCode.$invalid &&
          !this.v$.content.$invalid &&
          this.attachedFiles.length > 0
        );
      }
      return !this.v$.shortCode.$invalid && !this.v$.content.$invalid;
    },
    // Filter inboxes based on selected channel type
    availableInboxes() {
      if (this.interactiveChannelType === 'whatsapp') {
        // Show only WhatsApp inboxes
        return this.inboxesList.filter(
          inbox => inbox.channel_type === 'Channel::Whatsapp'
        );
      }
      if (this.interactiveChannelType === 'telegram') {
        // Show only Telegram inboxes
        return this.inboxesList.filter(
          inbox => inbox.channel_type === 'Channel::Telegram'
        );
      }
      if (this.interactiveChannelType === 'facebook') {
        // Show only Facebook inboxes
        return this.inboxesList.filter(
          inbox => inbox.channel_type === 'Channel::FacebookPage'
        );
      }
      if (this.interactiveChannelType === 'instagram') {
        // Show only Instagram inboxes
        return this.inboxesList.filter(
          inbox => inbox.channel_type === 'Channel::Instagram'
        );
      }
      // Show Web Widget and API inboxes
      return this.inboxesList.filter(
        inbox =>
          inbox.channel_type === 'Channel::WebWidget' ||
          inbox.channel_type === 'Channel::Api'
      );
    },
    selectedInbox() {
      if (!this.selectedInboxId) return null;
      return this.inboxesList.find(inbox => inbox.id === this.selectedInboxId);
    },
    fakeInboxForBuilder() {
      // If an inbox is selected, use it; otherwise create a fake one
      if (this.selectedInbox) {
        return this.selectedInbox;
      }
      // Fallback: Create a fake inbox object based on selected channel type
      let channelType = 'Channel::WebWidget'; // default
      if (this.interactiveChannelType === 'whatsapp') {
        channelType = 'Channel::Whatsapp';
      } else if (this.interactiveChannelType === 'telegram') {
        channelType = 'Channel::Telegram';
      } else if (this.interactiveChannelType === 'facebook') {
        channelType = 'Channel::FacebookPage';
      } else if (this.interactiveChannelType === 'instagram') {
        channelType = 'Channel::Instagram';
      }
      return {
        channel_type: channelType,
      };
    },
    effectiveInboxId() {
      // Return selected inbox ID, or 0 as fallback
      return this.selectedInboxId || 0;
    },
  },
  watch: {
    messageType(newType) {
      // Reset interactive payload when switching message types
      if (newType !== 'interactive') {
        this.interactivePayload = null;
      }
    },
    interactiveChannelType() {
      // Reset selected inbox when channel type changes
      this.selectedInboxId = null;
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.messageType = 'text';
      this.interactiveChannelType = 'web';
      this.selectedInboxId = null;
      this.interactivePayload = null;
      this.showInteractiveBuilder = false;
      this.attachedFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    openInteractiveBuilder() {
      console.log('Opening interactive builder modal');
      this.showInteractiveBuilder = true;
      console.log(
        'showInteractiveBuilder set to:',
        this.showInteractiveBuilder
      );
    },
    closeInteractiveBuilder() {
      this.showInteractiveBuilder = false;
    },
    handleInteractiveMessage(payload) {
      // Store the complete interactive message payload
      this.interactivePayload = payload;
      this.closeInteractiveBuilder();
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    addEmojiToContent(emoji) {
      this.content += emoji;
      this.hideEmojiPicker();
    },
    toggleRichTextToolbar() {
      this.showRichTextToolbar = !this.showRichTextToolbar;
    },
    onFileUpload(file) {
      const reader = new FileReader();
      reader.onload = e => {
        const base64Data = e.target.result.split(',')[1]; // Remove data:mime;base64, prefix
        const fileItem = {
          id: Date.now() + Math.random(), // temporary ID
          resource: file,
          thumb: file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : null,
          name: file.name,
          type: file.type,
          size: file.size,
          data: base64Data, // Store base64 data for backend
        };
        this.attachedFiles.push(fileItem);
      };
      reader.readAsDataURL(file);
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    onAttachFile() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        this.onFileUpload(file);
      });
      // Reset input to allow same file selection
      event.target.value = '';
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;

      // Prepare payload based on message type
      const payload = {
        short_code: this.shortCode,
        content: this.content,
      };

      if (this.messageType === 'interactive') {
        // Store the interactive payload with channel type
        payload.content_type = this.interactivePayload.content_type;
        payload.content_attributes = {
          ...this.interactivePayload.content_attributes,
          channel_type: this.interactiveChannelType, // 'web', 'whatsapp', 'telegram', or 'facebook'
        };
        payload.content = this.interactivePayload.message; // Message content from builder
      } else if (this.messageType === 'multimedia') {
        payload.content_type = 'multimedia';
        // Store file data for multimedia canned responses
        payload.content_attributes = {
          attachment_files: this.attachedFiles.map(file => ({
            name: file.name,
            type: file.type,
            size: file.size,
            data: file.data || null, // Base64 data will be added by file conversion
          })),
        };
      } else {
        payload.content_type = 'text';
      }

      // Make API Calls
      this.$store
        .dispatch('cannedResponse/createCannedResponse', payload)
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <!-- Message Type Selection -->
        <div class="w-full mb-4">
          <label class="block text-sm font-medium mb-2">
            {{ $t('CANNED_MGMT.ADD.FORM.MESSAGE_TYPE.LABEL') }}
          </label>
          <div class="flex gap-4">
            <label class="flex items-center">
              <input
                v-model="messageType"
                type="radio"
                value="text"
                class="mr-2"
              />
              <span>{{ $t('CANNED_MGMT.ADD.FORM.MESSAGE_TYPE.TEXT') }}</span>
            </label>
            <label class="flex items-center">
              <input
                v-model="messageType"
                type="radio"
                value="interactive"
                class="mr-2"
              />
              <span>{{
                $t('CANNED_MGMT.ADD.FORM.MESSAGE_TYPE.INTERACTIVE')
              }}</span>
            </label>
            <label class="flex items-center">
              <input
                v-model="messageType"
                type="radio"
                value="multimedia"
                class="mr-2"
              />
              <span>Multimedia</span>
            </label>
          </div>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>

          <!-- Text Message Editor -->
          <div v-if="messageType === 'text'" class="editor-wrap relative">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="[
                { editor_warning: v$.content.$error },
                { 'show-rich-text-toolbar': showRichTextToolbar },
              ]"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
            <div class="absolute top-2 right-2 flex gap-1 z-10">
              <button
                type="button"
                class="p-2 text-slate-500 hover:text-slate-700 focus:outline-none rounded"
                :class="{
                  'bg-slate-200 dark:bg-slate-700': showRichTextToolbar,
                }"
                :title="
                  showRichTextToolbar
                    ? 'Hide formatting toolbar'
                    : 'Show formatting toolbar'
                "
                @click.stop="toggleRichTextToolbar"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 4h12M6 8h12M6 12h12M6 16h12"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="p-2 text-slate-500 hover:text-slate-700 focus:outline-none rounded"
                @click.stop="toggleEmojiPicker"
              >
                😀
              </button>
            </div>
            <EmojiInput
              v-if="showEmojiPicker"
              v-on-clickaway="hideEmojiPicker"
              class="absolute top-12 right-0 z-50"
              :on-click="addEmojiToContent"
            />
          </div>

          <!-- Interactive Message Builder -->
          <div
            v-else-if="messageType === 'interactive'"
            class="interactive-message-builder"
          >
            <!-- Channel Type Selection -->
            <div class="mb-4">
              <label class="block text-sm font-medium mb-2">
                Channel Type
              </label>
              <div class="flex gap-4">
                <label class="flex items-center">
                  <input
                    v-model="interactiveChannelType"
                    type="radio"
                    value="web"
                    class="mr-2"
                  />
                  <span>Web Chat</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="interactiveChannelType"
                    type="radio"
                    value="whatsapp"
                    class="mr-2"
                  />
                  <span>WhatsApp</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="interactiveChannelType"
                    type="radio"
                    value="telegram"
                    class="mr-2"
                  />
                  <span>Telegram</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="interactiveChannelType"
                    type="radio"
                    value="facebook"
                    class="mr-2"
                  />
                  <span>Facebook</span>
                </label>
                <label class="flex items-center">
                  <input
                    v-model="interactiveChannelType"
                    type="radio"
                    value="instagram"
                    class="mr-2"
                  />
                  <span>Instagram</span>
                </label>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                This response template will only be available for the selected
                channel type.
              </p>
            </div>

            <!-- Inbox Selection (for WhatsApp catalogs/flows only) -->
            <div v-if="interactiveChannelType === 'whatsapp'" class="mb-4">
              <label class="block text-sm font-medium mb-2">
                Select Inbox
                <span class="text-xs font-normal text-gray-500">
                  (Required for catalogs/flows)
                </span>
              </label>
              <select v-model="selectedInboxId" class="form-control">
                <option :value="null" disabled>
                  {{
                    availableInboxes.length === 0
                      ? 'No inboxes available'
                      : 'Select an inbox'
                  }}
                </option>
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">
                Select the inbox that owns the catalogs or flows you want to
                use.
              </p>
            </div>

            <!-- Inbox Selection (for Telegram - optional) -->
            <div v-if="interactiveChannelType === 'telegram'" class="mb-4">
              <label class="block text-sm font-medium mb-2">
                Select Inbox
                <span class="text-xs font-normal text-gray-500">
                  (Optional)
                </span>
              </label>
              <select v-model="selectedInboxId" class="form-control">
                <option :value="null">
                  {{
                    availableInboxes.length === 0
                      ? 'No Telegram inboxes available'
                      : 'Select an inbox (optional)'
                  }}
                </option>
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">
                Optionally select a specific Telegram inbox for this template.
              </p>
            </div>

            <!-- Inbox Selection (for Facebook - optional) -->
            <div v-if="interactiveChannelType === 'facebook'" class="mb-4">
              <label class="block text-sm font-medium mb-2">
                Select Inbox
                <span class="text-xs font-normal text-gray-500">
                  (Optional)
                </span>
              </label>
              <select v-model="selectedInboxId" class="form-control">
                <option :value="null">
                  {{
                    availableInboxes.length === 0
                      ? 'No Facebook inboxes available'
                      : 'Select an inbox (optional)'
                  }}
                </option>
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">
                Optionally select a specific Facebook inbox for this template.
              </p>
            </div>

            <!-- Interactive Builder Button -->
            <div v-if="!interactivePayload" class="mb-4">
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent px-4 py-3 text-sm font-medium w-full shadow-lg"
                @click="openInteractiveBuilder"
              >
                Configure Interactive Message
              </button>
              <p class="text-xs text-gray-500 mt-2 text-center">
                Build your interactive message with options, cards, and more.
                Variables like &#123;&#123;customer_name&#125;&#125; are
                supported.
              </p>
            </div>

            <!-- Show Summary if Payload Exists -->
            <div
              v-else
              class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"
            >
              <div class="flex justify-between items-start mb-2">
                <div>
                  <h4 class="text-sm font-medium text-green-900">
                    ✓ Interactive Message Configured
                  </h4>
                  <p class="text-xs text-green-700 mt-1">
                    Type: {{ interactivePayload.content_type }}
                  </p>
                </div>
                <button
                  type="button"
                  class="text-sm text-blue-600 hover:text-blue-800"
                  @click="openInteractiveBuilder"
                >
                  Edit
                </button>
              </div>
              <div class="text-xs text-gray-600 mt-2">
                Message: {{ interactivePayload.message?.substring(0, 100)
                }}{{ interactivePayload.message?.length > 100 ? '...' : '' }}
              </div>
            </div>
          </div>

          <!-- Multimedia Message Editor -->
          <div
            v-else-if="messageType === 'multimedia'"
            class="multimedia-message-builder"
          >
            <!-- Message Content -->
            <div class="mb-4 relative">
              <WootMessageEditor
                v-model="content"
                class="message-editor [&>div]:px-1 min-h-[80px]"
                :class="[
                  { editor_warning: v$.content.$error },
                  { 'show-rich-text-toolbar': showRichTextToolbar },
                ]"
                enable-variables
                :enable-canned-responses="false"
                :enabled-menu-options="showRichTextToolbar ? undefined : []"
                :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
                @blur="v$.content.$touch"
              />
              <div class="absolute top-2 right-2 flex gap-1 z-10">
                <button
                  type="button"
                  class="p-2 text-slate-500 hover:text-slate-700 focus:outline-none rounded"
                  :class="{
                    'bg-slate-200 dark:bg-slate-700': showRichTextToolbar,
                  }"
                  :title="
                    showRichTextToolbar
                      ? 'Hide formatting toolbar'
                      : 'Show formatting toolbar'
                  "
                  @click.stop="toggleRichTextToolbar"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 4h12M6 8h12M6 12h12M6 16h12"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="p-2 text-slate-500 hover:text-slate-700 focus:outline-none rounded"
                  @click.stop="toggleEmojiPicker"
                >
                  😀
                </button>
              </div>
              <EmojiInput
                v-if="showEmojiPicker"
                v-on-clickaway="hideEmojiPicker"
                class="absolute top-12 right-0 z-50"
                :on-click="addEmojiToContent"
              />
            </div>

            <!-- File Upload Section -->
            <div class="mb-4">
              <div class="flex justify-between items-center mb-3">
                <h3 class="text-sm font-medium">Attachments</h3>
                <button
                  type="button"
                  class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-8 px-3 text-sm font-medium justify-center"
                  @click="onAttachFile"
                >
                  📎 Add File
                </button>
              </div>

              <!-- File Input (Hidden) -->
              <input
                ref="fileInput"
                type="file"
                multiple
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                class="hidden"
                @change="handleFileChange"
              />

              <!-- Attachment Preview -->
              <div
                v-if="attachedFiles.length > 0"
                class="max-h-32 overflow-y-auto"
              >
                <AttachmentPreview
                  :attachments="attachedFiles"
                  @remove-attachment="removeAttachment"
                />
              </div>

              <div
                v-if="attachedFiles.length === 0"
                class="text-sm text-gray-500 text-center py-2 border-2 border-dashed border-gray-200 rounded"
              >
                No files attached. Click "Add File" to attach files.
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="!isFormValid || addCanned.showLoading"
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>

      <!-- Interactive Message Builder Modal -->
      <InteractiveMessageBuilderModal
        v-model:show="showInteractiveBuilder"
        :inbox-id="effectiveInboxId"
        :inbox="fakeInboxForBuilder"
        @send="handleInteractiveMessage"
        @close="closeInteractiveBuilder"
      />
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .show-rich-text-toolbar .ProseMirror-menubar {
    @apply block;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
